<script type="ts">
    import { Tabs, Tab, TabContent, FileUploaderButton, Select, SelectItem, Button, Toggle, FormGroup } from "carbon-components-svelte";
    import TrashCan16 from "carbon-icons-svelte/lib/TrashCan16";
    import { stores } from "@nubolab-ffwd/svelte-fluent/src/internal/FluentProvider.svelte";
    import { Localized } from '@nubolab-ffwd/svelte-fluent';
    import { afterUpdate, onMount } from "svelte";
    import { getLocalizedValue, reduce } from "../helpers";
    import type SceneManager from "../scene-manager";
    import type { SceneItem } from "../scene-manager";
    import { SceneManagerEvent } from "../scene-manager";
    import Banner from "./banner.svelte";
    import Inventory from "./inventory.svelte";

    export let manager: SceneManager;
    export let langCallback: Function = null;

	const { getTranslation } = stores();

    let selectedItems: SceneItem[] = [];
    let selectedType: string | InventoryItem;
    let selectedName: string;
    let canTexture: boolean;
    let canBacklight: boolean;
    let canSpitshield: boolean;
    let canTextile: boolean;
    let textureAspect: [number, number];
    let textureResolution: [number, number];
    let typeCounter: Record<string, number> = {};
    let data: InventoryData = {};
    let selectedTab = 0;
    let lastClickedTab = 0;
    let showSelection = false;
    let selectedTexture = null;
    let selectedTextile = false;
    const dpcm = 2;

    function getType(type:string) : InventoryItem {
        return data.stand?.items?.find(i => i.type === type) ?? 
            data.furniture?.items?.find(i => i.type === type)
    }

    function getVariant(type:string, variant: string) {
        return getType(type).variants.find(v => v.variant === variant);
    }

    let langCode = $getTranslation("language-code").value;
    langCallback?.(langCode);

    $: {
        const selectedTypes = new Set<string>();
        selectedItems.forEach(i => selectedTypes.add(i.type));
        
        const selectedTypesMeta = new Map<string, InventoryItem>();
        selectedTypes.forEach(type => {
            selectedTypesMeta.set(
                type, 
                getType(type)
            );
        });

        selectedType = selectedItems.reduce<string>((agg, i) => !agg ? i.type : agg === i.type ? i.type : "multiple", null);
        selectedType = selectedType === "multiple" ? null : selectedTypesMeta.get(selectedType);
        
        selectedName = selectedItems.length === 1 
            ? getLocalizedValue(selectedItems[0], "name", langCode) 
            : selectedType 
                ? `${selectedItems.length} x ${typeof selectedType === "string" ? selectedType : getLocalizedValue(selectedType, "name", langCode)}` 
                : $getTranslation("inspector-multiple-selected").value

        const selectedTypesMetaArray = Array.from(selectedTypesMeta.values());
        canTexture = selectedTypesMetaArray.every(t => t.texture);
        canTextile = selectedTypesMetaArray.every(t => t.textile);
        canBacklight = selectedTypesMetaArray.every(t => t.backlight);
        canSpitshield = selectedTypesMetaArray.every(t => t.spitshield);

        const width = selectedItems.reduce((a, v) => Math.round((v.width ?? selectedTypesMeta.get(v.type)?.width ?? 0) * (selectedTypesMeta.get(v.type)?.width_factor ?? 1)) + a, 0);
        const height = selectedItems.reduce((a, v) => Math.max(v.height ?? selectedTypesMeta.get(v.type)?.height ?? 0, a), 0);
        textureAspect = canTexture ? reduce(width, height) : [1, 1];
        textureResolution = canTexture ? [width * dpcm, height * dpcm] : [100, 100];

        if (selectedItems.length) {
            selectedTexture = selectedItems[0].selectionOptions?.back ? selectedItems[0].texture2 : selectedItems[0].texture;
            selectedTextile = selectedItems[0].selectionOptions?.back ? selectedItems[0].textile2 : selectedItems[0].textile;
            selectedTab = 0;
            showSelection = true;
        } else {
            selectedTexture = null;
            selectedTextile = false;
            selectedTab = lastClickedTab;
            showSelection = false;
        }
    }
    
    onMount(async () => {
        data = await fetch(`./inventory.json`).then(res => res.json());
    });

    manager.addListener(SceneManagerEvent.Update, ({item, changed}) => {
        if(selectedItems.find(i => i.id === item.id)) {
            selectedItems = manager.selectedItems;
        }
    });

    manager.addListener(SceneManagerEvent.Select, ({items}) => {
        selectedItems = items ?? [];
    });

    function handleItemClick(item: InventoryItem) {
        if (item.floor) {
            handleFloorItemClick(item);
            return;
        }
        const num = (typeCounter[item.type] ?? 0) + 1;
        typeCounter[item.type] = num;
        let variant = undefined;
        let height = undefined;

        if (item.wall && item.variants) {
            variant = item.variants.find(v => v.height === manager.height);
            height = variant?.height || height;
            variant = variant?.variant;
        } else if (item.variants) {
            variant = item.variants[0].variant;
        }

        manager.putItem({
            type: item.type,
            name: `${getLocalizedValue(item, "name", langCode)} #${num}`,
            variant,
            height
        })
    }

    function handleFloorItemClick(item: InventoryItem) {
        manager.setFloor(item.type);
    }

    function handleTexture(event) {
        const file = event.currentTarget.files[0];
        const texture = manager.putTexture(file);
        const oldTextures = new Set<string>();

        selectedItems.sort((a, b) => a.w - b.w);
        let selectedWidths = selectedItems.map(i => {
            const variant = getVariant(i.type, i.variant);
            const type = getType(i.type);
            return (variant.width ?? type.width ?? 0) * (variant.width_factor ?? type.width_factor ?? 1);
        });
        const totalWidth = selectedWidths.reduce((a, w) => a + w, 0);
        selectedWidths = selectedWidths.map(w => w / totalWidth);

        const allBack = selectedItems.every(i => i.type == "Pannel_100x250" && i.selectionOptions?.back);

        selectedItems.forEach((i, idx) => {
            const key = i.selectionOptions?.back ? "texture2" : "texture";
            if (i[key] && i[key] !== texture) {
                oldTextures.add(i[key]);
            }
            const prefix = key.slice(0, 1) + key.slice(7);
            manager.putItem({
                ...i,
                [key]: texture,
                [`${prefix}Offset`]: (allBack ? selectedWidths.slice(idx + 1) : selectedWidths.slice(0, idx)).reduce((a,v) => a + v, 0),
                [`${prefix}Scale`]: selectedWidths[idx],
            })
        })

        oldTextures.forEach(t => manager.removeTexture(t));
    }

    function clearTexture() {
        const textures = new Set<string>();
        selectedItems.forEach((i) => {
            const key = i.selectionOptions?.back ? "texture2" : "texture";
            if (i[key]) {
                textures.add(i[key]);
                manager.putItem({
                    ...i,
                    [key]: undefined,
                    [`${key.slice(0, 1)}${key.slice(7)}Scale`]: undefined,
                    [`${key.slice(0, 1)}${key.slice(7)}Offset`]: undefined,
                })
            }
        })
        textures.forEach(t => manager.removeTexture(t));
    }

    function handleVariant(event) {
        const v = event.detail;
        if(v !== null) {
            selectedItems.forEach(i => {
                if (i.variant !== v) {
                    manager.putItem({
                        ...i,
                        variant: v,
                    })
                }
            })
        }
    }

    function handleBacklight(event) {
        const v = event.detail.toggled;
        selectedItems.forEach(i => {
            if(!!i.backlight !== !!v) {
                manager.putItem({
                    ...i,
                    backlight: event.detail.toggled,
                })
            }
        })  
    }

    function handleSpitshield(event) {
        selectedItems.forEach(i => {
            manager.putItem({
                ...i,
                spitshield: event.detail.toggled,
            })
        })    
    }

    function handleTextile(event) {
        const v = event.detail.toggled;
        selectedItems.forEach(i => {
            const key = i.selectionOptions?.back ? "textile2" : "textile";
            if(!!i[key] !== !!v) {
                manager.putItem({
                    ...i,
                    [key]: event.detail.toggled,
                })
            }
        })    
    }

    function blockClick(event) {
        event.stopPropagation();
    }

    let random = Math.random();
    function handleTabClick(num: number) {
        random = Math.random();
        lastClickedTab = num;
    }

</script>

<div class="inspector">
    {#key showSelection}
    <Tabs type="container" bind:selected={selectedTab}>
        {#if showSelection}<Tab on:click={() => handleTabClick(-1)}><Localized id="inspector-tab-selection" /></Tab>{/if}
        <Tab on:click={() => handleTabClick(0)}><Localized id="inspector-tab-booth" /></Tab>
        <Tab on:click={() => handleTabClick(1)}><Localized id="inspector-tab-furniture" /></Tab>
        <Tab on:click={() => handleTabClick(2)}><Localized id="inspector-tab-banner" /></Tab>
        <div slot="content">
            {#if showSelection}
            <TabContent>
            <div class="selected-box" on:click={blockClick}>
                <h2>{selectedName}</h2>
        
                {#if typeof selectedType !== "string" && selectedType?.variants}
                <FormGroup>
                    <Select labelText={$getTranslation("inspector-variants").value} selected={selectedItems.reduce((agg, i) => i.variant === agg ? agg : undefined, selectedItems[0].variant)} on:change={handleVariant}>
                        {#each selectedType?.variants as v}
                        <SelectItem value={v.variant} text={getLocalizedValue(v, "name", langCode)} />
                        {/each}
                    </Select>
                </FormGroup>
                {/if}
        
                {#if canTexture}
                <FormGroup legendText={$getTranslation("inspector-texture").value}>
                    <div class="filepicker">
                        <FileUploaderButton
                            labelText={selectedTexture ? selectedTexture : $getTranslation("inspector-select-file").value}
                            accept={['.jpg', '.jpeg', '.png']}
                            on:change={handleTexture}
                        />
                        {#if selectedTexture}
                        <Button
                            kind="ghost"
                            iconDescription={$getTranslation("inspector-delete").value}
                            icon={TrashCan16}
                            on:click={() => clearTexture()} />
                        {/if}
                    </div>
                    <p>
                        {$getTranslation("inspector-texture-hint").value}
                        <br>
                        <strong>{$getTranslation("inspector-texture-sizing", { aspect: textureAspect.join(':'), resolution: textureResolution.join('x') }).value}</strong>
                    </p>
                    {#if canTextile}
                    <div class="subitem">
                        <Toggle
                            labelText={$getTranslation("inspector-textile").value}
                            on:toggle={ev => handleTextile(ev)}
                            labelA={$getTranslation("inspector-no").value}
                            labelB={$getTranslation("inspector-yes").value}
                            toggled={!!selectedTextile} 
                        />
                    </div>
                    {/if}
                </FormGroup>
                {/if}
        
                {#if canBacklight}
                <FormGroup>
                    <Toggle 
                        labelText={$getTranslation("inspector-backlight").value}
                        on:toggle={handleBacklight} 
                        labelA={$getTranslation("inspector-no").value}
                        labelB={$getTranslation("inspector-yes").value}
                        toggled={!!selectedItems[0].backlight} 
                    />
                </FormGroup>
                {/if}
        
                {#if canSpitshield}
                <FormGroup>
                    <Toggle 
                        labelText={$getTranslation("inspector-spitshield").value} 
                        on:toggle={handleSpitshield} 
                        labelA={$getTranslation("inspector-no").value}
                        labelB={$getTranslation("inspector-yes").value}
                        toggled={!!selectedItems[0].spitshield} 
                    />
                </FormGroup>
                {/if}
            </div>
            </TabContent>
            {/if}
            <TabContent>
                <Inventory reset={random} inventory={data.stand} onItemClick={handleItemClick} />
            </TabContent>
            <TabContent>
                <Inventory reset={random} inventory={data.furniture} onItemClick={handleItemClick} />
            </TabContent>
            <TabContent>
                <Banner manager={manager} />
            </TabContent>
        </div>
    </Tabs>
    {/key}
</div>

<style type="text/scss">
    .inspector {
        height: 100%;
        display: flex;
        flex-direction: column;

        [slot="content"] {
            overflow: hidden;
            flex-grow: 1;
            flex-shrink: 1;
        }
    }

    .filepicker {
        display: flex;
    }

    .selected-box {
        margin: 0 -1rem;
        padding: 0 1rem;

        h2 {
            font-size: 1.25rem;
            margin-bottom: .5rem;
        }

        p {
            font-size: .875rem;
            padding: .25rem 0;
        }
    }

    .subitem {
        margin: .75rem 0 0;
    }
</style>